import { recoverNewbiePending, validNewbiePeriod } from './newbiePackageRecovery.mjs';
import { reactive } from 'vue';
import { gameId, newGameRequestId, uuidPattern } from './gameplay.mjs';
import { adaptCampaignModel, campaignKind, campaignType } from './activityCampaignModels.js';
import { adaptActivityTopic } from './activityTopicLive.js';

export function validCampaignId(value) {
  return typeof value === 'number' ? Number.isSafeInteger(value) && value > 0
    : typeof value === 'string' && /^[1-9]\d*$/.test(value);
}

export async function resolveCampaignActivity(api, kind, id) {
  const type = campaignType(kind);
  if (!type) throw new Error('活动类型无效');
  let selected = id;
  if (id != null && id !== '' && !validCampaignId(id)) throw new Error('活动编号无效');
  if (id == null || id === '') {
    const list = await api.list();
    if (!Array.isArray(list)) throw new Error('活动列表暂不可用');
    selected = list.find(row => row?.activityType === type && validCampaignId(row.id))?.id;
    if (!selected) return null;
  }
  const activity = await api.detail(selected);
  if (!activity || String(activity.id) !== String(selected) || activity.activityType !== type) throw new Error('该活动与当前页面类型不符');
  return activity;
}

const positiveMoney = value => typeof value === 'string' && /^\d{1,12}(?:\.\d{1,2})?$/.test(value) && Number(value) > 0;
const paidClaim = (row, id) => validCampaignId(row?.id) && String(row.activityId) === String(id)
  && Number(row.status) === 2 && positiveMoney(row.rewardAmount);
// Page instances share operation outcomes. A timeout stays locked until both
// state and a paid receipt prove completion, including after leaving the page.
const sharedDepositOperations = new Map();
const confirmedReceipt = (model, history, id) => model?.claimed === true && Array.isArray(history?.data) && history.data.some(row => paidClaim(row, id) && (!model.venueRequired || String(row.id) === String(model.claimRecordId)));

export function createActivityCampaign({ api, getToken, getMemberKey = getToken, operations = sharedDepositOperations, notify = () => {}, dark = () => false, packageStorage = { get: () => '', set: () => { throw new Error('无法保存礼包请求编号'); }, remove() {} }, requestId = newGameRequestId, withPackageLock }) {
  const state = reactive({ activity: null, model: null, kind: '', loading: false, busy: '', error: '', loggedIn: false, unresolved: false });
  let active = true, revision = 0, identity = '', request = null, lastInput = null;
  const venueKey = input => input?.kind === 'deposit' && validCampaignId(input.venueId) ? `:venue:${input.venueId}` : '';
  const keyFor = input => `${input?.kind}:${input?.activity?.id || ''}:${getToken()}${venueKey(input)}`;
  const operationKey = input => `${getMemberKey()}:${input?.activity?.id || ''}${venueKey(input)}`;
  const current = (version, key) => active && version === revision && identity === key && keyFor(lastInput) === key;
  const newbieTargets = ['_package','newbie-deposit-gift','newbie-withdrawal-gift','newbie-first-game-gift','newbie-game-gift','newbie-revival-gift',...Array.from({length:20},(_,index)=>`newbie-first-game-gift-level-${index+1}`)];
  const newbieOperationKey = (input,target='_package',periodKey='') => `newbie:${operationKey(input)}${target === '_package' ? '' : ':'+target}${periodKey ? ':day:'+periodKey : ''}`;
  function newbieStorageKey(input,target='_package') {
    const actor = gameId(getMemberKey()), id = gameId(input?.activity?.id);
    if (!actor || !id) throw new Error('会员标识待核对，请重新登录');
    return `kg_newbie_package:v1:${actor}:${id}${target === '_package' ? '' : ':'+target}`;
  }
  function pendingNewbie(input,target='_package') {
    const value = packageStorage.get(newbieStorageKey(input,target));
    if (!value) return '';
    if (typeof value !== 'string' || !uuidPattern.test(value)) throw new Error('上次礼包请求编号异常，请保留记录并联系客服');
    return value;
  }
  function originalNewbie(model, target) { return target === '_package' ? model?.package : model?.gifts?.find(row=>row.key===target); }
  function confirmedNewbie(model,target='_package',expected='') {
    const gift = originalNewbie(model,target);
    return Boolean(gift && (gift.claimed === true && gift.completedAt > 0 || (gift.manualReview && gift.reviewStatus || gift.venueReward && gift.payoutStatus) && !gift.claimed && !gift.completedAt) && gameId(gift.recordId) && uuidPattern.test(gift.requestId) && (!expected || gift.requestId.toLowerCase() === expected.toLowerCase()));
  }
  // An explicit action may restore the opposite client's original application.
  // Compare its actual amount (zero before approval) and the authoritative readback.
  function confirmedNewbieAction(model, target, response, input) {
    if (response?.targetKey !== target || String(response.state?.activityId) !== String(input.activity.id)
      || response.state?.activityType !== 'NEWBIE_TASK' || !confirmedNewbie(model,target)) return false;
    const original = originalNewbie(model,target);
    const written = target === '_package' ? response.state.newbiePackage : response.state.newbieGifts?.find(row=>row.key===target);
    const expectedStatus = original.claimed ? 'COMPLETED' : original.reviewStatus === 'REJECTED' ? 'REJECTED' : original.payoutStatus === 'FAILED' ? 'FAILED' : 'PENDING';
    return response.status === expectedStatus && written?.claimed === original.claimed && written.eligible === false
      && (written.reviewStatus || '') === (original.reviewStatus || '') && (written.payoutStatus || '') === (original.payoutStatus || '')
      && String(response.recordId) === original.recordId && String(written.recordId) === original.recordId
      && written.requestId === original.requestId && written.completedAt === original.completedAt
      && written.rewardAmount === original.rewardAmount && response.rewardAmount === (original.claimed ? original.rewardAmount : '0.00');
  }
  function reset(input, model = null) {
    Object.assign(state, { activity: input.activity || null, model, kind: input.kind, loading: false, busy: '', error: '', loggedIn: Boolean(getToken()), unresolved: false });
  }
  async function readModel(input) {
    if (input.kind === 'topic') return adaptActivityTopic(await api.topic(), { dark: dark() });
    if (input.kind === 'newcomer') {
      const [progress, registration] = await Promise.allSettled([api.newbiePackage ? api.newbiePackage(input.activity.id) : api.state(input.activity.id), api.registrationDays?.()]);
      if (progress.status !== 'fulfilled') throw progress.reason;
      const model = adaptCampaignModel(input.activity, progress.value, { dark: dark() });
      const fact = registration.status === 'fulfilled' ? registration.value : null;
      if (model.package?.claimed !== true && fact?.timeZone === 'Asia/Shanghai' && Number.isSafeInteger(fact.registeredAt) && fact.registeredAt > 0 && fact.registeredAt < 253402185600) {
        model.registrationDate = new Date((fact.registeredAt + 8 * 3600) * 1000).toISOString().slice(0,19).replace('T',' ');
      }
      return model;
    }
    const raw = input.kind === 'deposit' ? await (validCampaignId(input.venueId) ? api.firstDeposit(input.activity.id, { venueId: input.venueId }) : api.firstDeposit(input.activity.id)) : await api.state(input.activity.id);
    return adaptCampaignModel(input.activity, raw, { dark: dark() });
  }
  async function verifyDepositReceipt(model, history, input) {
    if (confirmedReceipt(model, history, input.activity.id)) return true;
    if (!model?.venueRequired || model.claimed !== true || !validCampaignId(model.claimRecordId)) return false;
    try {
      const original = await api.records({ activityId: input.activity.id, recordId: model.claimRecordId, pageNumber: 1, pageSize: 1 });
      return confirmedReceipt(model, original, input.activity.id);
    } catch { return false; }
  }
  async function load(input = lastInput) {
    if (!input || !['newcomer', 'vote', 'deposit', 'topic'].includes(input.kind)) return;
    const key = keyFor(input);
    if (request?.key === key && active && identity === key) return request.promise;
    const version = ++revision;
    if (identity !== key) {
      const sameDeposit = input.kind === 'deposit' && lastInput?.kind === input.kind && String(lastInput.activity?.id) === String(input.activity?.id) && keyFor(lastInput) === identity;
      reset(input, sameDeposit ? state.model : null);
    }
    identity = key; lastInput = input;
    state.kind = input.kind; state.activity = input.activity || null; state.loggedIn = Boolean(getToken());
    state.error = '';
    if (!active || !state.loggedIn) { state.model = null; state.loading = false; return; }
    if (input.kind !== 'topic' && (!validCampaignId(input.activity?.id) || campaignKind(input.activity.activityType) !== input.kind)) {
      state.model = null; state.error = '活动类型或编号无效'; return;
    }
    state.loading = true;
    const operation = input.kind === 'deposit' ? operations.get(operationKey(input)) : input.kind === 'newcomer' ? newbieTargets.map(target=>operations.get(newbieOperationKey(input,target,state.model?.gifts?.find(row=>row.key===target)?.periodKey || ''))).find(operation=>operation?.status==='pending') : null;
    if (operation?.status === 'pending') {
      state.busy = 'claim';
      await operation.promise;
      if (current(version, key)) { state.busy = ''; return load(input); }
      return;
    }
    const promise = (async () => {
      try {
        const [modelResult, history] = await Promise.allSettled([
          readModel(input), input.kind === 'deposit' && operation ? Promise.resolve().then(() => api.records({ activityId: input.activity.id, pageNumber:1, pageSize:50 })) : Promise.resolve(null)
        ]);
        if (!current(version, key)) return;
        if (modelResult.status !== 'fulfilled') throw modelResult.reason;
        state.model = modelResult.value;
        if (input.kind === 'newcomer' && (state.model.package?.enabled || state.model.gifts?.length)) {
          state.unresolved = false;
          for (const target of newbieTargets) {
            let pending = pendingNewbie(input,target);
            const period = state.model.gifts?.find(row=>row.key===target)?.periodKey || '';
            const saved = operations.get(newbieOperationKey(input,target,period));
            let confirmed = confirmedNewbie(state.model,target,pending);
            if (pending && !confirmed && target !== '_package' && (period || packageStorage.get(newbieStorageKey(input,target)+':period'))) {
              const storedPeriod = packageStorage.get(newbieStorageKey(input,target)+':period') || '';
              const original = await recoverNewbiePending({ recoverRequest: api.recover, id:input.activity.id, target, requestId:pending, periodKey:storedPeriod, today:period });
              if (!current(version,key)) return;
              if (original) {
                const pendingKey = newbieStorageKey(input,target);
                if (packageStorage.get(pendingKey) === pending) { packageStorage.remove(pendingKey); packageStorage.remove(pendingKey+':period'); }
                operations.delete(newbieOperationKey(input,target,storedPeriod));
                pending = ''; // Historical recovery never marks today's gift as paid.
              }
            }
            if (confirmed) {
              if (saved) saved.status = 'confirmed';
              if (pending && packageStorage.get(newbieStorageKey(input,target)) === pending) { packageStorage.remove(newbieStorageKey(input,target)); packageStorage.remove(newbieStorageKey(input,target)+':period'); }
            }
            if (!confirmed && (pending || saved)) state.unresolved = true;
          }
        } else {
          if (operation && await verifyDepositReceipt(state.model, history.status === 'fulfilled' ? history.value : null, input)) operation.status = 'confirmed';
          if (!current(version, key)) return;
          state.unresolved = Boolean(operation && (operation.status !== 'confirmed' || state.model.claimed !== true));
        }
        if (state.unresolved) state.error = '领取结果尚待核对，请稍后刷新状态和记录';
      } catch (error) {
        if (current(version, key)) state.error = error.message || '活动状态查询失败，请重试';
      } finally {
        if (current(version, key)) state.loading = false;
        if (request?.promise === promise) request = null;
      }
    })();
    request = { key, promise };
    return promise;
  }
  async function claimFirstDeposit() {
    const input = lastInput, key = keyFor(input), model = state.model, writeKey = operationKey(input);
    const venueId = model?.venueRequired ? Number(input?.venueId) : 0;
    if (!active || key !== identity || !state.loggedIn || !input || input.kind !== 'deposit' || state.loading || state.busy || state.error || state.unresolved || operations.has(writeKey)
      || model?.venueRequired && (!validCampaignId(input.venueId) || !Number.isSafeInteger(venueId) || venueId <= 0 || String(input.venueId) !== String(model.venueId))
      || model?.eligible !== true || model?.claimed !== false || !positiveMoney(model.rewardAmount)) return false;
    const version = revision;
    const token = getToken();
    const operation = { status:'pending', promise:null };
    operations.set(writeKey, operation);
    state.busy = 'claim';
    const promise = (async () => {
      let failure = null;
      try { if (model.venueRequired) await api.claim(input.activity.id, { venueId }); else await api.claim(input.activity.id); } catch (error) { failure = error; }
      operation.status = 'unresolved';
      if (getToken() !== token) return false;
      // A write response or timeout is followed by authoritative readback, never
      // another claim. Hidden pages retain the per-member operation lock.
      const results = await Promise.allSettled([readModel(input), Promise.resolve().then(() => api.records({ activityId: input.activity.id, pageNumber: 1, pageSize: 50 }))]);
      const modelResult = results[0], recordsResult = results[1];
      const verified = modelResult.status === 'fulfilled' && await verifyDepositReceipt(modelResult.value, recordsResult.status === 'fulfilled' ? recordsResult.value : null, input);
      const confirmed = getToken() === token && verified;
      if (confirmed) operation.status = 'confirmed';
      if (!current(version, key)) return false;
      if (modelResult.status === 'fulfilled') state.model = modelResult.value;
      if (confirmed) {
        state.unresolved = false; state.error = ''; notify('首存奖励已到账', 'success'); return true;
      }
      state.unresolved = true;
      state.error = failure?.message || '领取结果尚待核对，请刷新状态后查看';
      notify(state.error);
      return false;
    })().finally(() => {
      if (operation.status === 'pending') operation.status = 'unresolved';
      if (identity === key) state.busy = '';
    });
    operation.promise = promise;
    return promise;
  }
  async function claimNewbiePackageInternal(target='_package', retry=false) {
    const input = lastInput, key = keyFor(input), token = getToken();
    const gift = target === '_package' ? null : state.model?.gifts?.find(row=>row.key===target);
    const original = originalNewbie(state.model,target);
    const resumeVenue = original?.venueReward === true && original.payoutStatus === 'READY';
    const canClaim = target === '_package' ? state.model?.canClaim === true : (gift?.eligible === true || resumeVenue) && gift.claimed === false;
    if (!newbieTargets.includes(target) || !active || key !== identity || input?.kind !== 'newcomer' || !token || state.loading || state.busy || (!retry && (state.error || state.unresolved || !canClaim))) return false;
    const periodKey = gift?.periodKey || '';
    const writeKey = newbieOperationKey(input,target,periodKey);
    if (resumeVenue && operations.get(writeKey)?.status === 'confirmed') operations.delete(writeKey);
    if (operations.has(writeKey) && (!retry || operations.get(writeKey).status === 'pending')) { state.unresolved = true; state.error = '已有礼包请求待核对，请刷新查询'; return false; }
    let storageKey, uuid, submittedPeriod = periodKey;
    try {
      storageKey = newbieStorageKey(input,target);
      const pending = pendingNewbie(input,target);
      if (retry && !pending) return false;
      if (!retry && pending) { state.unresolved = true; state.error = '上次礼包领取待核对，请刷新查询'; return false; }
      uuid = retry ? pending : resumeVenue ? original.requestId : requestId();
      if (!uuidPattern.test(uuid)) throw new Error('无法生成有效礼包请求编号');
      if (retry) submittedPeriod = packageStorage.get(storageKey+':period') || '';
      else {
        if (gift?.taskCycle === 2 && !validNewbiePeriod(periodKey)) throw Error('每日领取日期待核对，请刷新');
        if (periodKey) packageStorage.set(storageKey+':period', periodKey);
        packageStorage.set(storageKey, uuid);
      }
      if (submittedPeriod && packageStorage.get(storageKey+':period') !== submittedPeriod) throw Error('每日领取日期未保存，未提交领取');
      if (packageStorage.get(storageKey) !== uuid) throw new Error('礼包请求编号未能保存，未提交领取');
    } catch (error) { state.error = error.message; return false; }
    const version = revision, operation = { status: 'pending', promise: null };
    operations.set(writeKey, operation); state.busy = target === '_package' ? 'claim' : 'gift:'+target;
    const promise = (async () => {
      let failure = null, actionResponse;
      try { actionResponse = await api.claimNewbie(input.activity.id, { action: 'CLAIM', targetKey: target, requestId: uuid, ...(submittedPeriod ? { periodKey:submittedPeriod } : {}) }); }
      catch (error) { failure = error; }
      operation.status = 'unresolved';
      if (getToken() !== token) return false;
      let model;
      try { model = await readModel(input); } catch (error) { failure ||= error; }
      let pastNotCommitted = false;
      let confirmed = getToken() === token && (confirmedNewbie(model,target,uuid) || confirmedNewbieAction(model,target,actionResponse,input));
      if (!confirmed && submittedPeriod && getToken() === token) {
        try {
          const original=await recoverNewbiePending({recoverRequest:api.recover,id:input.activity.id,target,requestId:uuid,periodKey:submittedPeriod,today:model?.gifts?.find(row=>row.key===target)?.periodKey || ''});
          pastNotCommitted = original?.absentPastPeriod === true && getToken() === token;
          confirmed=Boolean(original) && !pastNotCommitted && getToken()===token;
        } catch (error) { failure ||= error; }
      }
      if (confirmed || pastNotCommitted) {
        operation.status = 'confirmed';
        if (packageStorage.get(storageKey) === uuid) { packageStorage.remove(storageKey); packageStorage.remove(storageKey+':period'); }
      }
      if (!current(version, key)) return false;
      if (model) state.model = model;
      state.unresolved = !confirmed && !pastNotCommitted;
      state.error = confirmed || pastNotCommitted ? '' : failure?.message || '礼包领取结果尚待核对，请刷新查询';
      if (confirmed) {
        const original = originalNewbie(model,target);
        const notice = original?.claimed ? confirmedNewbie(model,target,uuid) ? original.venueReward ? `已到账至${original.venueName}` : '已到账' : '已领取，已恢复原结果' : original?.reviewStatus === 'REJECTED' ? '审核未通过，请查看审核说明' : original?.payoutStatus === 'FAILED' ? '场馆原指令明确失败，未发放奖励' : original?.payoutStatus === 'READY' ? '原申请已保留，可继续原场馆发放' : original?.venueReward && original.payoutStatus === 'PENDING' ? '场馆原指令处理中，请刷新核对' : original?.reviewStatus === 'APPROVED' ? '审核已通过，等待发放' : '申请已提交，等待审核';
        notify(`${gift?.title || '新人礼包'}${notice}`, 'success');
      }
      if (pastNotCommitted) notify('领取日期已更新，原请求未入账，请重新确认领取');
      return confirmed;
    })().finally(() => {
      if (operation.status === 'pending') operation.status = 'unresolved';
      if (identity === key) state.busy = '';
    });
    operation.promise = promise;
    return promise;
  }
  async function claimNewbiePackage(target='_package', retry=false) {
    if (!withPackageLock) return claimNewbiePackageInternal(target,retry);
    const input = lastInput, actor = getMemberKey(), token = getToken();
    if (input?.kind !== 'newcomer' || !active) return false;
    return withPackageLock(`kg-newbie:${actor}:${input.activity.id}`, acquired => {
      if (!active || getMemberKey() !== actor || getToken() !== token) return false;
      if (!acquired) { state.error = '其它页面正在核对礼包，请稍后刷新'; return false; }
      return claimNewbiePackageInternal(target,retry);
    }).catch(error => { if (active) state.error = error.message || '暂时无法核对礼包'; return false; });
  }
  async function retryNewbiePackage() {
    if (!state.unresolved || state.busy || state.loading || lastInput?.kind !== 'newcomer') return false;
    try {
      const target = newbieTargets.find(target=>pendingNewbie(lastInput,target));
      return target ? claimNewbiePackage(target,true) : false;
    } catch (error) { state.error = error.message; return false; }
  }
  function suspend() { active = false; ++revision; request = null; state.loading = false; }
  function resume() { active = true; }
  return { state, load, claimFirstDeposit, claimNewbiePackage, retryNewbiePackage, suspend, resume, dispose: suspend };
}
